/**
 * Service Provider Signup Page
 *
 * This page informs users that provider registration requires invite codes
 * to maintain high-quality standards in our vetted provider network.
 * Users can choose to either request an invite code or register with an existing code.
 */

/* eslint-disable react/no-unescaped-entities */

import { useNavigate } from 'react-router-dom';

export function ServiceProviderSignup() {
  const navigate = useNavigate();

  const handleRequestInvite = () => {
    navigate('/provider/request-invite');
  };

  const handleRegisterWithCode = () => {
    navigate('/provider/register');
  };

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-neutral-900 mb-2">
              Join Our Vetted Provider Network
            </h1>
            <p className="text-neutral-600 text-lg">
              We require invite codes to ensure only qualified, verified service providers join our marketplace.
            </p>
          </div>

          {/* Main Card */}
          <div className="card">
            <div className="border-b border-neutral-200 pb-4 mb-6">
              <h2 className="text-xl font-semibold text-neutral-900">Get Started</h2>
              <p className="text-sm text-neutral-600 mt-1">
                Choose how you'd like to proceed
              </p>
            </div>

            {/* Info Section */}
            <div className="mb-6">
              <div className="p-4 bg-primary-50 rounded-lg">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-primary-900 mb-2">
                      Why Invite Codes?
                    </h3>
                    <p className="text-sm text-primary-800">
                      Invite codes help us maintain the highest standards in our waste management network.
                      Every provider is verified before joining, ensuring waste generators can trust they're
                      working with qualified professionals. This vetting process creates better matches between
                      providers and county-specific requirements.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <button
                onClick={handleRequestInvite}
                className="btn-primary w-full py-3 text-base"
              >
                Request an Invite Code
              </button>

              <button
                onClick={handleRegisterWithCode}
                className="btn-secondary w-full py-3 text-base"
              >
                I Have an Invite Code
              </button>
            </div>
          </div>

          {/* What Happens Next Box */}
          <div className="mt-6 p-4 bg-neutral-100 rounded-lg">
            <h3 className="text-sm font-semibold text-neutral-900 mb-2">What happens next?</h3>
            <ul className="space-y-2 text-sm text-neutral-700">
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Submit your request with detailed company information</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Our team reviews your qualifications and will be in touch soon</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Upon approval, receive your invite code via email</span>
              </li>
              <li className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Complete registration and join our trusted provider network</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
